/**
 * ===================================================
 * Module: frontend.src.types.project.ts
 * Description: Project and session related type definitions
 * Owner: Kawada Technosystem Co Ltd
 * All Rights Reserved
 * ===================================================
 */

export interface Project {
  id: string;
  name: string;
  description?: string;
  user_id: string;
  created_at: string;
  updated_at: string;
}

export interface Session {
  id: string;
  name: string;
  description?: string;
  pdf_filename: string;
  filename?: string;
  pdf_size_bytes: number;
  pdf_url?: string;
  status:
  | "waiting_for_upload"
  | "uploading"
  | "queued"
  | "processing"
  | "completed"
  | "failed"
  | "cancelled"
  | "expired";
  project_id: string;
  created_at: string;
  updated_at?: string;
  // Additional fields for detailed session info
  status_message?: string;
  last_updated?: string;
  num_pages?: number;
  thumbnail_data?: string;
  thumbnail_content_type?: string;
  progress?: number;
  queue_position?: number;
  retention_days?: number;
  expires_at?: string;
  editing_status?: string | null;
}

// Define inactive statuses for easy checking
export const INACTIVE_SESSION_STATUSES = ['completed', 'failed', 'cancelled', 'expired'] as const;
export type InactiveSessionStatus = typeof INACTIVE_SESSION_STATUSES[number];

// Helper function to check if session is active
export const isSessionActive = (status: Session['status']): boolean => {
  return !INACTIVE_SESSION_STATUSES.includes(status as InactiveSessionStatus);
};

export interface Job {
  id: string;
  session_id: string;
  job_type: "pdf_processing" | "redaction_analysis";
  status: "pending" | "in_progress" | "completed" | "failed";
  result?: any;
  error?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateProjectRequest {
  name: string;
  description?: string;
}

export interface CreateSessionRequest {
  name: string;
  description?: string;
  filename: string;
  file_size_bytes: number;
  file_page_count?: number;
  thumbnail_data?: string;
  thumbnail_content_type?: string;
  redaction_classes?: string[];
  retention_days?: number;
  selected_pages?: number[] | null; // null = all pages, array = specific pages
}

// SessionsList component interface
export interface SessionsListProps {
  sessions: Session[];
  onSessionClick: (sessionId: string) => void;
  isLoading: boolean;
  isSelectionMode?: boolean;
  selectedSessions?: Set<string>;
  onSessionSelect?: (sessionId: string, isSelected: boolean) => void;
  onCancelSelection?: () => void;
}
